import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IoDocumentText, IoTime, IoPeople, IoSync, IoArrowBack } from 'react-icons/io5';
import ReactMarkdown from 'react-markdown';
import '../style/Groups.css';
import Modal from './Modal';
import { listTranscriptSessions, getTranscriptSession, regenerateTranscriptSummary } from '../api/TranscriptAPI';
import { useLanguage } from '../i18n/LanguageContext';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useSocket } from '../socket/SocketContext';

const username = () => localStorage.getItem('username');
const token = () => localStorage.getItem('accessToken');

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}dk ${String(s).padStart(2, '0')}sn`;
};

// summaryStatus -> the i18n key for its badge label. 'ready' isn't listed -
// the actual summary text is shown instead of a status badge for that one
// (see the detail pane below).
const SUMMARY_STATUS_KEY = {
  pending: 'transcript.summaryPending',
  failed: 'transcript.summaryFailed',
  skipped: 'transcript.summarySkipped'
};

// Must match .TranscriptHistoryLayout's `transition: height` duration in
// Groups.css - this is when the inline height pin (see the layout effect
// below) gets released back to auto, so it needs to line up with when the
// CSS transition has actually finished animating.
const HEIGHT_TRANSITION_MS = 240;

// Past-sessions browser for a room, opened from its RoomCard in Groups.js -
// independent of VoiceRoomPanel/the currently-occupied room, since history
// should be readable for any room the caller was ever a participant in (the
// backend enforces that - see transcriptController.js's
// loadSessionForParticipant - this component just renders whatever it gets
// back). Same {iShow,setiShow}-controlled Modal pattern as MusicSearchModal,
// but roomId is supplied by the opener per-click rather than read from a
// single always-current room.
export default function TranscriptHistoryModal({ mState, roomId }) {
  const { t } = useLanguage();
  // Phones show one pane at a time: the session list, then (after a tap)
  // that session full-screen with a back button - two columns side by side
  // left the detail ~150px wide there.
  const { isMobile } = useLayoutMode();
  const { pushToast } = useSocket();
  const [sessions, setSessions] = useState(null); // null = not loaded yet
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [detail, setDetail] = useState(null); // {session, segments} | null
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const layoutRef = useRef(null);
  // Height (px) the two-column layout was pinned to right before switching
  // sessions - null means "let CSS size it naturally", non-null means
  // "an animated resize toward the new content's height is in flight".
  const [frozenHeight, setFrozenHeight] = useState(null);

  useEffect(() => {
    if (!mState.iShow || !roomId) return;
    setSessions(null);
    setSelectedSessionId(null);
    setDetail(null);
    setFrozenHeight(null);
    setLoadingSessions(true);
    listTranscriptSessions(username(), token(), roomId).then((res) => {
      setLoadingSessions(false);
      setSessions(res.status === 'success' ? res.data.sessions : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mState.iShow, roomId]);

  const openSession = async (sessionId) => {
    // Pin the layout to whatever height it's currently at before swapping
    // in the loading state, so the modal doesn't shrink down to the tiny
    // "loading..." placeholder and then grow again once the new session's
    // content arrives - it only resizes once, straight to the final height.
    if (layoutRef.current) {
      setFrozenHeight(layoutRef.current.getBoundingClientRect().height);
    }
    setSelectedSessionId(sessionId);
    setDetail(null);
    setLoadingDetail(true);
    const res = await getTranscriptSession(username(), token(), sessionId);
    setLoadingDetail(false);
    setDetail(res.status === 'success' ? res.data : null);
  };

  const regenerateSummary = async () => {
    if (!detail || regenerating) return;
    const requestedSessionId = detail.session.sessionId;
    setRegenerating(true);
    const res = await regenerateTranscriptSummary(username(), token(), requestedSessionId);
    setRegenerating(false);

    if (res.status === 'success') {
      // Functional update, not a stale closure over `detail` - the user may
      // have already clicked over to a different session while this request
      // was in flight, in which case the response is for a session that's
      // no longer the one on screen and must be dropped, not applied.
      setDetail((prev) => (prev && prev.session.sessionId === requestedSessionId
        ? { ...prev, session: { ...prev.session, summary: res.data.summary, summaryStatus: res.data.summaryStatus } }
        : prev));
      pushToast(t('transcript.summaryRegenerateSuccess'));
    } else {
      pushToast(t('transcript.summaryRegenerateFailed'));
    }
  };

  // Runs once the new session's content has actually landed in the DOM
  // (skipped while still loading, so the pinned height from openSession()
  // holds steady through the wait). Measures the content's natural height
  // by briefly releasing the pin, then animates from the pinned height to
  // that target via the CSS transition on .TranscriptHistoryLayout, instead
  // of the instant snap that plain content-driven auto-height gives.
  useLayoutEffect(() => {
    const el = layoutRef.current;
    if (!el || loadingDetail || frozenHeight === null) return undefined;

    el.style.transition = 'none';
    el.style.height = 'auto';
    const targetHeight = el.getBoundingClientRect().height;
    el.style.height = `${frozenHeight}px`;
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight; // force a reflow so the pin above commits before the transition is re-enabled
    el.style.transition = '';

    const raf = requestAnimationFrame(() => {
      el.style.height = `${targetHeight}px`;
    });
    const settle = setTimeout(() => {
      el.style.height = '';
      setFrozenHeight(null);
    }, HEIGHT_TRANSITION_MS);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, [detail, loadingDetail, frozenHeight]);

  return (
    <Modal mState={mState} buttonShow className="TranscriptHistoryModalContainer">
      <h4 style={{ marginTop: 0 }}>{t('transcript.historyTitle')}</h4>

      <div
        className={`TranscriptHistoryLayout${selectedSessionId ? ' TranscriptHistoryHasSelection' : ''}`}
        ref={layoutRef}
        style={frozenHeight !== null ? { height: `${frozenHeight}px` } : undefined}
      >
        <ul className="TranscriptHistoryList">
          {loadingSessions && <li className="descGray">{t('transcript.historyLoading')}</li>}
          {!loadingSessions && sessions && sessions.length === 0 && (
            <li className="descGray">{t('transcript.historyEmpty')}</li>
          )}
          {!loadingSessions && sessions && sessions.map((s) => (
            <li
              key={s._id}
              className={`glassCard TranscriptHistoryItem ${selectedSessionId === s._id ? 'TranscriptHistoryItemActive' : ''}`}
              onClick={() => openSession(s._id)}
            >
              <IoDocumentText className="TranscriptHistoryItemIcon" />
              <div className="TranscriptHistoryItemInfo">
                <b>{new Date(s.startedAt).toLocaleString()}</b>
                <aside className="descGray">
                  <IoTime /> {formatDuration(s.durationSec) || '-'}
                  {typeof s.segmentCount === 'number' && ` · ${s.segmentCount}`}
                </aside>
              </div>
            </li>
          ))}
        </ul>

        <div className="TranscriptHistoryDetail">
          {isMobile && selectedSessionId && (
            <button type="button" className="TranscriptHistoryBack" onClick={() => { setSelectedSessionId(null); setDetail(null); }}>
              <IoArrowBack /> {t('common.back')}
            </button>
          )}
          {!selectedSessionId && <p className="descGray">{t('transcript.historySelectPrompt')}</p>}
          {selectedSessionId && loadingDetail && <p className="descGray">{t('transcript.historyLoading')}</p>}
          {selectedSessionId && !loadingDetail && !detail && <p className="descGray">{t('transcript.historyLoadFailed')}</p>}

          {selectedSessionId && !loadingDetail && detail && (
            <>
              <div className="TranscriptHistoryMeta descGray">
                <span><IoPeople /> {detail.session.participants.map((p) => p.username).join(', ')}</span>
              </div>

              <div className="TranscriptHistorySummary">
                <div className="TranscriptHistorySummaryHeader">
                  <button
                    type="button"
                    className="TranscriptSummaryRegenerateButton"
                    onClick={regenerateSummary}
                    disabled={regenerating}
                  >
                    <IoSync className={regenerating ? 'MusicConnectingSpinner' : undefined} />
                    {t(regenerating ? 'transcript.summaryRegenerating' : 'transcript.summaryRegenerate')}
                  </button>
                </div>
                {detail.session.summaryStatus === 'ready' && detail.session.summary ? (
                  <ReactMarkdown>{detail.session.summary.text}</ReactMarkdown>
                ) : (
                  <p className="descGray">{t(SUMMARY_STATUS_KEY[detail.session.summaryStatus] || 'transcript.summaryPending')}</p>
                )}
              </div>

              <div className="TranscriptLines TranscriptHistorySegments">
                {detail.segments.map((seg) => (
                  <div key={seg.seq} className="TranscriptLine">
                    <b className="TranscriptLineSpeaker">{seg.username}</b>
                    <span className="TranscriptLineText">{seg.text}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <button onClick={() => mState.setiShow(false)} className="SendButton secondaryColor">{t('common.close')}</button>
      </div>
    </Modal>
  );
}
